import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

const ManualBlockModal = ({ onClose, onSave, currentWeekStart }) => {
  const [formData, setFormData] = useState({
    date: '',
    startTime: '09:00',
    endTime: '10:00',
    reason: '',
    isRecurring: false,
    recurringDays: [], // Array of day names: ['Monday', 'Wednesday', etc.]
    recurringEndDate: ''
  });

  // Generate time options (30-minute increments from 7 AM to 7 PM)
  const timeOptions = [];
  for (let hour = 7; hour <= 19; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      if (hour === 19 && minute > 0) break; // Stop at 7:00 PM
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeOptions.push(timeStr);
    }
  }

  // Generate date options for next 30 days
  const dateOptions = [];
  for (let i = 0; i < 30; i++) {
    const date = new Date();
    date.setDate(date.getDate() + i);
    dateOptions.push({
      value: date.toISOString().split('T')[0],
      label: date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      })
    });
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (formData.isRecurring) {
      // Validate recurring fields
      if (formData.recurringDays.length === 0) {
        alert('Please select at least one day for recurring blocks');
        return;
      }
      if (!formData.recurringEndDate) {
        alert('Please select an end date for recurring blocks');
        return;
      }
    } else {
      // Validate single date
      if (!formData.date || !formData.startTime || !formData.endTime) {
        alert('Please fill in all required fields');
        return;
      }
    }

    // Validate that end time is after start time
    if (formData.endTime <= formData.startTime) {
      alert('End time must be after start time');
      return;
    }

    const blocks = [];

    if (formData.isRecurring) {
      // Generate recurring blocks
      const startDate = new Date();
      const endDate = new Date(formData.recurringEndDate);
      
      const dayMap = {
        'Sunday': 0,
        'Monday': 1,
        'Tuesday': 2,
        'Wednesday': 3,
        'Thursday': 4,
        'Friday': 5,
        'Saturday': 6
      };
      
      // Generate blocks for each matching day between start and end date
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        const dayName = Object.keys(dayMap).find(key => dayMap[key] === d.getDay());
        
        if (formData.recurringDays.includes(dayName)) {
          const dateStr = d.toISOString().split('T')[0];
          
          // Generate 30-minute blocks for this day
          let currentTime = formData.startTime;
          while (currentTime < formData.endTime) {
            const [hours, minutes] = currentTime.split(':').map(Number);
            const nextMinutes = minutes + 30;
            let nextTime;
            
            if (nextMinutes >= 60) {
              nextTime = `${(hours + 1).toString().padStart(2, '0')}:${(nextMinutes - 60).toString().padStart(2, '0')}`;
            } else {
              nextTime = `${hours.toString().padStart(2, '0')}:${nextMinutes.toString().padStart(2, '0')}`;
            }
            
            blocks.push({
              date: dateStr,
              start: currentTime,
              end: nextTime,
              reason: formData.reason || 'Recurring Block'
            });
            
            currentTime = nextTime;
          }
        }
      }
    } else {
      // Generate blocks for single date
      let currentTime = formData.startTime;
      
      while (currentTime < formData.endTime) {
        const [hours, minutes] = currentTime.split(':').map(Number);
        const nextMinutes = minutes + 30;
        let nextTime;
        
        if (nextMinutes >= 60) {
          nextTime = `${(hours + 1).toString().padStart(2, '0')}:${(nextMinutes - 60).toString().padStart(2, '0')}`;
        } else {
          nextTime = `${hours.toString().padStart(2, '0')}:${nextMinutes.toString().padStart(2, '0')}`;
        }
        
        blocks.push({
          date: formData.date,
          start: currentTime,
          end: nextTime,
          reason: formData.reason || 'Blocked'
        });
        
        currentTime = nextTime;
      }
    }

    onSave(blocks);
  };

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-black border-4 border-[var(--accent)] w-full max-w-lg">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-[var(--accent)]">[BLOCK TIME MANUALLY]</h2>
            <button
              onClick={onClose}
              className="text-[var(--accent)] hover:text-white"
            >
              <X size={24} />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Recurring Toggle */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isRecurring}
                  onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                  className="w-5 h-5 bg-black border-2 border-[var(--accent)]"
                />
                <span className="text-[var(--accent)] font-bold">RECURRING BLOCK</span>
              </label>
            </div>

            {!formData.isRecurring ? (
              /* Single Date Selection */
              <div>
                <label className="block text-[var(--accent)] font-bold mb-2">
                  DATE: <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                  required={!formData.isRecurring}
                >
                  <option value="">-- Select Date --</option>
                  {dateOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              /* Recurring Options */
              <>
                <div>
                  <label className="block text-[var(--accent)] font-bold mb-2">
                    REPEAT ON DAYS: <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                      <label key={day} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.recurringDays.includes(day)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({ ...formData, recurringDays: [...formData.recurringDays, day] });
                            } else {
                              setFormData({ ...formData, recurringDays: formData.recurringDays.filter(d => d !== day) });
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span className="text-[var(--accent)] text-sm">{day}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-[var(--accent)] font-bold mb-2">
                    REPEAT UNTIL: <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.recurringEndDate}
                    onChange={(e) => setFormData({ ...formData, recurringEndDate: e.target.value })}
                    className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                    required={formData.isRecurring}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
              </>
            )}

            {/* Start Time */}
            <div>
              <label className="block text-[var(--accent)] font-bold mb-2">
                START TIME: <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                required
              >
                {timeOptions.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>

            {/* End Time */}
            <div>
              <label className="block text-[var(--accent)] font-bold mb-2">
                END TIME: <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
                required
              >
                {timeOptions.map(time => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>

            {/* Reason/Note */}
            <div>
              <label className="block text-[var(--accent)] font-bold mb-2">
                REASON/NOTE:
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)] min-h-[100px]"
                placeholder="Optional: Enter reason for blocking this time..."
              />
            </div>

            {/* Info Text */}
            <div className="text-sm text-[var(--accent)]/70 bg-[var(--accent)]/10 border border-[var(--accent)]/30 p-3">
              ℹ️ This will block all 30-minute time slots between the start and end times you selected.
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3"
              >
                [SAVE BLOCK]
              </Button>
              <Button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3"
              >
                [CANCEL]
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ManualBlockModal;
